import { buildPlacementLadder } from './build-placement-ladder';
import { getCountertopRun } from './get-countertop-run';
import { CountertopSeamGroup, getCountertopSeamGroups } from './get-countertop-seam-groups';
import { getCountertopSections } from './get-countertop-sections';
import { getSeamDemands } from './get-seam-demands';
import { hasCountertopSurface } from './has-countertop-surface';
import { MAX_UNSEAMED_SECTION_LENGTH, SEAM_POSITION_TOLERANCE } from '../constants';

import type { BreakpointPlacementCandidate, BreakpointPlacementStep, CountertopSection, SeamDemand } from '../types';
import type { Breakpoints } from '../breakpoints.class';
import type { Countertop } from '../../countertops/countertop.class';

export type PlaceRequiredBreakpointsParams = {
	countertop: Countertop;
	/** Defaults to the documented 88" cap. */
	maxSectionLength?: number;
};

export type PlaceRequiredBreakpointsResult = {
	added: number;
	removed: number;
	/** Which rung of the ladder each committed placement came from, in order. */
	steps: Array<BreakpointPlacementStep>;
	/** Demands no rung of the ladder could express -- the validation still warns about these. */
	unresolved: Array<SeamDemand>;
};

// A run cannot need more seams than it has 88" stretches; the bound only exists so a
// pathological geometry stops the loop instead of spinning.
const MAX_PASSES = 32;

const demandKey = (demand: SeamDemand): string => [demand.reason, demand.anchorGroupId ?? '', demand.section.start, demand.section.end].join('|');

const isInsideSection = (center: number, section: CountertopSection): boolean => {
	return center > section.start + SEAM_POSITION_TOLERANCE && center < section.end - SEAM_POSITION_TOLERANCE;
};

const maxSubSectionLength = (centers: Array<number>, section: CountertopSection): number => {
	const sections = getCountertopSections({ run: { left: section.start, right: section.end, length: section.length }, centers });
	return sections.reduce((acc, subSection) => Math.max(acc, subSection.length), 0);
};

// appliance-anchored demands are position-pinned, so they are satisfied first; among
// length demands the longest section is split first so each pass removes the worst offender
const nextDemand = (demands: Array<SeamDemand>): SeamDemand => {
	return demands.find((demand) => demand.reason === 'granite-appliance')
		?? [...demands].sort((a, b) => b.section.length - a.section.length)[0];
};

const isPlaceable = (params: { candidate: BreakpointPlacementCandidate; section: CountertopSection; occupied: Array<number> }): boolean => {
	const { candidate, section, occupied } = params;

	return candidate.centers.every((center) => isInsideSection(center, section))
		&& candidate.centers.every((center) => !occupied.some((existing) => Math.abs(existing - center) <= SEAM_POSITION_TOLERANCE));
};

const chooseCandidate = (params: { demand: SeamDemand; groups: Array<CountertopSeamGroup>; occupied: Array<number>; maxSectionLength: number }): BreakpointPlacementCandidate | null => {
	const { demand, groups, occupied, maxSectionLength } = params;
	const { section } = demand;

	const ladder = buildPlacementLadder({ section, groups, anchorGroupId: demand.anchorGroupId })
		.filter((candidate) => isPlaceable({ candidate, section, occupied }));

	const satisfying = ladder.find((candidate) => maxSubSectionLength(candidate.centers, section) <= maxSectionLength + SEAM_POSITION_TOLERANCE);
	if (satisfying) return satisfying;

	// A granite appliance seam is required regardless of size, so one of the appliance's own rungs
	// has to be taken even when none of them gets both sides under the cap. Take whichever leaves
	// the shortest worst side and let the length demand it leaves behind be solved next pass.
	if (demand.reason === 'granite-appliance') {
		return ladder.reduce((best: BreakpointPlacementCandidate | null, candidate) => {
			if (!best) return candidate;
			return maxSubSectionLength(candidate.centers, section) < maxSubSectionLength(best.centers, section) ? candidate : best;
		}, null);
	}

	// Nothing gets this section under the cap in one go. Accept the last rung -- the thirds
	// split -- only while it still shortens the worst sub-section, which guarantees the loop ends.
	const fallback = ladder[ladder.length - 1] ?? null;
	if (!fallback) return null;

	return maxSubSectionLength(fallback.centers, section) < section.length ? fallback : null;
};

const commitCandidate = (params: { breakpoints: Breakpoints; candidate: BreakpointPlacementCandidate; section: CountertopSection }): number => {
	const { breakpoints, candidate, section } = params;
	let placed = 0;

	candidate.centers.forEach((center) => {
		if (!isInsideSection(center, section)) return;
		if (breakpoints.children.some((breakpoint) => Math.abs(breakpoint.center - center) <= SEAM_POSITION_TOLERANCE)) return;

		const breakpoint = breakpoints.new(center);
		breakpoint.ruleManaged = true;
		placed++;
	});

	return placed;
};

/**
 * Drops rule-generated seams the design no longer needs, judged one at a time against what
 * would remain. A seam a designer has since moved still counts as satisfying its demand, so
 * it survives; a hand-placed seam is never a candidate for removal.
 */
const pruneStale = (params: { countertop: Countertop; maxSectionLength: number }): number => {
	const { countertop, maxSectionLength } = params;
	const { breakpoints } = countertop;
	let removed = 0;

	[...breakpoints.children].filter((breakpoint) => breakpoint.ruleManaged).forEach((candidate) => {
		const centers = breakpoints.children.filter((breakpoint) => breakpoint.id !== candidate.id).map((breakpoint) => breakpoint.center);
		if (getSeamDemands({ countertop, maxSectionLength, centers }).length > 0) return;
		if (breakpoints.delete(candidate.id)) removed++;
	});

	return removed;
};

/**
 * Walks the demands one at a time, each pass splitting the worst outstanding section, until
 * nothing is left to satisfy. A demand no rung can express is recorded and set aside so the
 * remaining demands still get served.
 */
const placeForDemands = (params: { countertop: Countertop; maxSectionLength: number; result: PlaceRequiredBreakpointsResult }): void => {
	const { countertop, maxSectionLength, result } = params;
	const { breakpoints } = countertop;
	const groups = getCountertopSeamGroups(countertop);
	const abandoned = new Set<string>();

	for (let pass = 0; pass < MAX_PASSES; pass++) {
		const demands = getSeamDemands({ countertop, maxSectionLength }).filter((demand) => !abandoned.has(demandKey(demand)));
		if (demands.length === 0) return;

		const demand = nextDemand(demands);
		const occupied = breakpoints.children.map((breakpoint) => breakpoint.center);
		const candidate = chooseCandidate({ demand, groups, occupied, maxSectionLength });
		const placed = candidate ? commitCandidate({ breakpoints, candidate, section: demand.section }) : 0;

		if (placed === 0) {
			abandoned.add(demandKey(demand));
			result.unresolved.push(demand);
			continue;
		}

		result.added += placed;
		result.steps.push(candidate.step);
	}
};

/**
 * Adds the countertop seams the material rules require, placing each one by the documented
 * fall-through order (see `buildPlacementLadder`), then removes rule-generated seams the design
 * has outgrown. Hand-placed breakpoints are read as satisfying demands and are never touched.
 * @param params the countertop to seam and the cap to apply
 * @returns how many seams were added and removed, plus any demand the model could not place
 */
export const placeRequiredBreakpoints = (params: PlaceRequiredBreakpointsParams): PlaceRequiredBreakpointsResult => {
	const { countertop, maxSectionLength = MAX_UNSEAMED_SECTION_LENGTH } = params;
	const result: PlaceRequiredBreakpointsResult = { added: 0, removed: 0, steps: [], unresolved: [] };

	const { breakpoints = null } = countertop ?? {};
	if (!breakpoints) return result;

	if (hasCountertopSurface(countertop) && getCountertopRun(countertop)) placeForDemands({ countertop, maxSectionLength, result });

	result.removed = pruneStale({ countertop, maxSectionLength });

	return result;
};
